import {
  Currency,
  type SupportedPaymentCurrency,
} from 'domains-billing-currency';

export enum AdminFeeAmountType {
  AdminFeeAmountTypeUnknown = 'ADMIN_FEE_AMOUNT_TYPE_UNKNOWN',
  AdminFeeAmountTypeFixed = 'ADMIN_FEE_AMOUNT_TYPE_FIXED',
  AdminFeeAmountTypePercentage = 'ADMIN_FEE_AMOUNT_TYPE_PERCENTAGE',
}

export interface AdminFee {
  value: number;
  adminFeeAmountType: AdminFeeAmountType;
}

export interface AdminFeeTax {
  rate: number;
  taxAmountType: TaxAmountType;
}

export interface PriceComponents {
  amount: number;
  markup?: Markup;
  adminFee?: AdminFee;
  adminFeeTax?: AdminFeeTax;
  tax?: Tax;
  netPrice?: number;
  sellingPrice?: SellingPrice;
}

export enum InvoiceType {
  InvoiceTypeMonthly = 'INVOICE_TYPE_MONTHLY',
  InvoiceTypeNoInvoice = 'INVOICE_TYPE_NO_INVOICE',
  InvoiceTypeOneoff = 'INVOICE_TYPE_ONEOFF',
  InvoiceTypeUnkown = 'INVOICE_TYPE_UNKOWN',
}

export enum gqlProductType {
  ProductTypeCreditBundle = 'PRODUCT_TYPE_CREDIT_BUNDLE',
  ProductTypeServiceLabour = 'PRODUCT_TYPE_SERVICE_LABOUR',
  ProductTypeServiceMaterials = 'PRODUCT_TYPE_SERVICE_MATERIALS',
  ProductTypeUnknown = 'PRODUCT_TYPE_UNKNOWN',
}

export type ProductTax = {
  __typename?: 'ProductTax';
  /** The dollar value of the tax */
  taxAmountType: TaxAmountType;
  /** The dollar value of the tax */
  value: Scalars['Float']['output'];
};

export type ProductTaxInput = {
  taxAmountType: TaxAmountType;
  value: Scalars['Float']['input'];
};

export enum GroupType {
  GroupTypeActivateChannel = 'GROUP_TYPE_ACTIVATE_CHANNEL',
  GroupTypeActivateWhitelabel = 'GROUP_TYPE_ACTIVATE_WHITELABEL',
  GroupTypeUnknown = 'GROUP_TYPE_UNKNOWN',
}

export enum gqlRecurrenceInterval {
  RecurrenceIntervalDaily = 'RECURRENCE_INTERVAL_DAILY',
  RecurrenceIntervalMonthly = 'RECURRENCE_INTERVAL_MONTHLY',
  RecurrenceIntervalUnknown = 'RECURRENCE_INTERVAL_UNKNOWN',
  RecurrenceIntervalWeekly = 'RECURRENCE_INTERVAL_WEEKLY',
  RecurrenceIntervalYearly = 'RECURRENCE_INTERVAL_YEARLY',
}

export type gqlMarkup = {
  __typename?: 'Markup';
  /** The type of the markup - fixed or percentage */
  markupAmountType: MarkupAmountType;
  /** The dollar value of the markup */
  value: Scalars['Float']['output'];
};

export type MarkupInput = {
  markupAmountType: MarkupAmountType;
  value: Scalars['Float']['input'];
};

export enum MarkupAmountType {
  MarkupAmountTypeFixed = 'MARKUP_AMOUNT_TYPE_FIXED',
  MarkupAmountTypePercentage = 'MARKUP_AMOUNT_TYPE_PERCENTAGE',
  MarkupAmountTypeUnknown = 'MARKUP_AMOUNT_TYPE_UNKNOWN',
}

/**
 * The price that the seller applied to the product.
 * This price can be composed of flat amounts or percentages.
 * This price SHOULD NOT be displayed to purchasers. See: SellingPrice
 */
export type Price = {
  __typename?: 'Price';
  /** The rate of the product without any tax or markup */
  amount: Scalars['Float']['output'];
  /** The markup applied to the product */
  markup?: Maybe<Markup>;
  /** The tax applied to the product */
  tax?: Maybe<ProductTax>;
};

export enum TaxAmountType {
  TaxAmountTypeFixed = 'TAX_AMOUNT_TYPE_FIXED',
  TaxAmountTypePercentage = 'TAX_AMOUNT_TYPE_PERCENTAGE',
  TaxAmountTypeUnknown = 'TAX_AMOUNT_TYPE_UNKNOWN',
}

export type gqlSubscriptionDetails = {
  __typename?: 'SubscriptionDetails';
  anyoneCanCancel: Scalars['Boolean']['output'];
  channelId: Scalars['String']['output'];
  createdAt: Scalars['DateTime']['output'];
  createdBy: Scalars['String']['output'];
  endDate?: Maybe<Scalars['DateTime']['output']>;
  intervalCount: Scalars['Int']['output'];
  recurrenceInterval: RecurrenceInterval;
  subscriptionDetailsId: Scalars['String']['output'];
  totalCounts?: Maybe<Scalars['Int']['output']>;
  updatedAt: Scalars['DateTime']['output'];
  updatedBy: Scalars['String']['output'];
};

export enum RecurrenceStatus {
  Active = 'ACTIVE',
  Cancelled = 'CANCELLED',
  Expired = 'EXPIRED',
}

export const INVOICEID_NO_INVOICE = 'no_invoice';

export type Receivable = {
  __typename?: 'Receivable';
  /** The details of the receivable when type is credit bundle */
  creditBundle?: Maybe<CreditBundle>;
  /** The type of the receivable */
  type?: Maybe<ReceivableType>;
};

export enum ReceivableType {
  ReceivableTypeCreditBundle = 'RECEIVABLE_TYPE_CREDIT_BUNDLE',
  ReceivableTypeUnknown = 'RECEIVABLE_TYPE_UNKNOWN',
}

export type Maybe<T> = T | null;

export type Transaction = {
  __typename?: 'Transaction';
  amount: Scalars['Float']['output'];
  billingAddress?: Maybe<BillingAddress>;
  channelId: Scalars['String']['output'];
  companyName?: Maybe<Scalars['String']['output']>;
  createdAt?: Maybe<Scalars['DateTime']['output']>;
  currency: Currency;
  externalEntityId?: Maybe<Scalars['String']['output']>;
  externalEntityType?: Maybe<ExternalEntityType>;
  paidBy: Scalars['String']['output'];
  paymentSource?: Maybe<PaymentSource>;
  paymentType: PaymentMethod;
  productItems?: Maybe<Array<Maybe<ProductItem>>>;
  productName: Scalars['String']['output'];
  status: Scalars['String']['output'];
  taxAmount?: Maybe<Scalars['Float']['output']>;
  taxId?: Maybe<Scalars['String']['output']>;
  transactionId: Scalars['String']['output'];
  updatedAt?: Maybe<Scalars['DateTime']['output']>;
};

export type Scalars = {
  ID: { input: string; output: string };
  String: { input: string; output: string };
  Boolean: { input: boolean; output: boolean };
  Int: { input: number; output: number };
  Float: { input: number; output: number };
  AnalyticType: { input: any; output: any };
  BigInt: { input: any; output: any };
  Binary: { input: any; output: any };
  Byte: { input: any; output: any };
  Color: { input: any; output: any };
  ContentCategoryType: { input: any; output: any };
  Date: { input: any; output: any };
  DateTime: { input: any; output: any };
  EmailAddress: { input: any; output: any };
  EventType: { input: any; output: any };
  GeoPoint: { input: any; output: any };
  Image: { input: any; output: any };
  JSON: { input: any; output: any };
  MediaContentType: { input: any; output: any };
  Path: { input: any; output: any };
  Permission: { input: any; output: any };
  PhoneNumber: { input: any; output: any };
  PositiveInt: { input: any; output: any };
  PostalCode: { input: any; output: any };
  Requirement: { input: any; output: any };
  StudioGroupRoleName: { input: any; output: any };
  SupportedLocale: { input: any; output: any };
  URL: { input: any; output: any };
  UUID: { input: any; output: any };
  UserContentInteractionStatus: { input: any; output: any };
  Void: { input: any; output: any };
};

type BillingAddress = {
  addressLine1: string;
  addressLine2: string;
  city: string;
  country: string;
  postalCode: string;
  state: string;
};

export type OrderSummaryItemType = {
  name: string;
  description: string;
  quantity: number | null;
  dollarAmount: string;
  subscriptionDetails?: {
    interval: gqlRecurrenceInterval;
    endDate?: Date;
  };
};

export type OrderSummaryTaxType = {
  key: string;
  name: string;
  rate: string;
  amount: string;
};

export type OrderSummaryType = {
  items: OrderSummaryItemType[];
  subTotal: string;
  taxes: OrderSummaryTaxType[];
  total: string;
  currency: string;
  subscriptionDetails?: {
    interval: gqlRecurrenceInterval;
    endDate?: Date;
  };
};

export type OrderSummaryWithProducts = {
  products: Product[];
  cashTotal: number;
  creditTotal: number;
  taxTotal: number;
  tax?: Tax;
  currency: string;
  subscriptionDetails?: gqlSubscriptionDetails;
};

export enum RecurrenceInterval {
  RECURRENCE_INTERVAL_UNKNOWN = 'RECURRENCE_INTERVAL_UNKNOWN',
  RECURRENCE_INTERVAL_DAILY = 'RECURRENCE_INTERVAL_DAILY',
  RECURRENCE_INTERVAL_WEEKLY = 'RECURRENCE_INTERVAL_WEEKLY',
  RECURRENCE_INTERVAL_MONTHLY = 'RECURRENCE_INTERVAL_MONTHLY',
  RECURRENCE_INTERVAL_YEARLY = 'RECURRENCE_INTERVAL_YEARLY',
  UNRECOGNIZED = 'UNRECOGNIZED',
}

export enum RecurrenceType {
  RECURRENCE_TYPE_CHARGES = 'RECURRENCE_TYPE_CHARGES',
  RECURRENCE_TYPE_UNKNOWN = 'RECURRENCE_TYPE_UNKNOWN',
}

/*
 ** @deprecated Please use ProductType from the billing-and-payment library for web usage, or ProductType from the billing library when working on the server side
 */
export enum ProductType {
  PRODUCT_TYPE_UNKNOWN = 'PRODUCT_TYPE_UNKNOWN',
  PRODUCT_TYPE_SERVICE_LABOUR = 'PRODUCT_TYPE_SERVICE_LABOUR',
  PRODUCT_TYPE_SERVICE_MATERIALS = 'PRODUCT_TYPE_SERVICE_MATERIALS',
  PRODUCT_TYPE_CREDIT_BUNDLE = 'PRODUCT_TYPE_CREDIT_BUNDLE',
}

export enum PaymentMethod {
  PAYMENT_METHOD_UNKNOWN = 'PAYMENT_METHOD_UNKNOWN',
  PAYMENT_METHOD_CREDIT_CARD = 'PAYMENT_METHOD_CREDIT_CARD',
  PAYMENT_METHOD_CREDITS = 'PAYMENT_METHOD_CREDITS',
  UNRECOGNIZED = 'UNRECOGNIZED',
}

export enum ExternalUserSource {
  EXTERNAL_USER_SOURCE_UNKNOWN = 'EXTERNAL_USER_SOURCE_UNKNOWN',
  EXTERNAL_USER_SOURCE_ACTIVATE = 'EXTERNAL_USER_SOURCE_ACTIVATE',
  UNRECOGNIZED = 'UNRECOGNIZED',
}

export interface CreateUserRequest {
  externalUserId: string;
  externalUserSource: ExternalUserSource;
}

export interface CreateUserResponse {
  accountsUserId: string;
  externalUserSource: ExternalUserSource;
  externalUserId: string;
  paymentProcessorCustomerId: string;
}

export interface GetUserByExternalIdRequest {
  externalUserId: string;
  externalUserSource: ExternalUserSource;
}

export interface GetUserByExternalIdResponse {
  accountsUserId: string;
  externalUserSource: ExternalUserSource;
  externalUserId: string;
  paymentProcessorCustomerId: string;
  paymentSources: PaymentSource[];
  invoiceAutopayment: {
    isInvoiceAutopaymentEnabled: boolean;
    paymentSource: PaymentSource;
  };
}

export type PaymentSource = {
  paymentMethodId: string;
  method: PaymentMethod;
  brand: string;
  expiryMonth: number;
  expiryYear: number;
  last4: string;
};

export interface SavePaymentMethodRequest {
  accountsUserId: string;
  paymentProcessorSourceId: string;
}

export interface SavePaymentMethodResponse {
  paymentProcessorCustomerId: string;
  paymentProcessorSourceId: string;
}

export enum ExternalPayerType {
  EXTERNAL_PAYER_TYPE_UNKNOWN = 'EXTERNAL_PAYER_TYPE_UNKNOWN',
  EXTERNAL_PAYER_TYPE_ACTIVATE_USER = 'EXTERNAL_PAYER_TYPE_ACTIVATE_USER',
  EXTERNAL_PAYER_TYPE_ACTIVATE_COMPANY = 'EXTERNAL_PAYER_TYPE_ACTIVATE_COMPANY',
}

export enum ExternalEntityType {
  EXTERNAL_ENTITY_TYPE_UNKNOWN = 'EXTERNAL_ENTITY_TYPE_UNKNOWN',
  EXTERNAL_ENTITY_TYPE_WORKORDER = 'EXTERNAL_ENTITY_TYPE_WORKORDER',
  EXTERNAL_ENTITY_TYPE_ACTIVATE_UCI = 'EXTERNAL_ENTITY_TYPE_ACTIVATE_UCI',
  EXTERNAL_ENTITY_TYPE_ACTIVATE_INVOICE = 'EXTERNAL_ENTITY_TYPE_ACTIVATE_INVOICE',
}

export enum ExternalActorType {
  EXTERNAL_ACTOR_TYPE_UNKNOWN = 'EXTERNAL_ACTOR_TYPE_UNKNOWN',
  EXTERNAL_ACTOR_TYPE_ACTIVATE_USER = 'EXTERNAL_ACTOR_TYPE_ACTIVATE_USER',
}

export type ProductForCharge = Omit<Product, 'productCategory'> & {};
export interface ProductItemForCharge {
  product: ProductForCharge;
  quantity: number;
  productCategoryId: string;
  chargeCodeId: string;
}

export interface CreateChargeRequest {
  name: string;
  description: string;
  currency: Currency;
  amount: number;
  externalPayerId: string;
  externalPayerType: ExternalPayerType;
  groupId: string;
  groupType: GroupType;
  externalEntityId: string;
  externalEntityType: ExternalEntityType;
  externalActorId: string;
  externalActorType: ExternalActorType;
  metadata: string;
  invoiceType: InvoiceType;
  invoiceDueDate?: Date;
  items: ProductItemForCharge[];
  isDraft?: boolean;
}

export interface CreateRecurringChargeRequest {
  charge: {
    name: string;
    description: string;
    currency: Currency;
    amount: number;
    externalPayerId: string;
    externalPayerType: ExternalPayerType;
    groupId: string;
    groupType: GroupType;
    externalEntityId: string;
    externalEntityType: ExternalEntityType;
    externalActorId: string;
    externalActorType: ExternalActorType;
    metadata: string;
    invoiceType: InvoiceType;
    invoiceDueDate?: Date;
    invoiceDueInDays?: number;
    items: ProductItemForCharge[];
  };
  recurrenceType: RecurrenceType;
  recurrenceInterval: RecurrenceInterval;
  startDate: Date;
  endDate?: Date;
  intervalCount?: number;
  totalCounts?: number;
  anyoneCanCancel: boolean;
}

export interface CreateProductCategoryRequest {
  productType: ProductType;
  name: string;
  description: string;
  groupId: string;
  groupType: GroupType;
}

export interface RemovePaymentMethodRequest {
  accountsUserId: string;
  paymentProcessorSourceId: string;
}

export interface RemovePaymentMethodResponse {
  accountsUserId: string;
}

export interface GetTransactionStatusResponse {
  transactionStatus: string;
  transactionId: string;
}

export enum TransactionPaidStatus {
  SUCCESS = 'SUCCEEDED',
  REFUNDED = 'REFUND_SUCCEEDED',
  FAILURE = 'FAILED',
}

export interface InvoicePartial {
  id: string;
  invoiceDisplayId: string;
  status: string;
  invoiceDate: Date | undefined;
  dueDate: Date | undefined;
  createdAt: Date | undefined;
  updatedAt: Date | undefined;
}

export interface Charge {
  id: string;
  name: string;
  description: string;
  amount: number;
  currency: Currency;
  status: string;
  invoiceId: string;
  externalPayerId: string;
  externalPayerType: ExternalPayerType;
  groupId: string;
  groupType: GroupType;
  externalEntityId: string;
  externalEntityType: ExternalEntityType;
  externalActorId: string;
  externalActorType: ExternalActorType;
  items: ProductItem[];
  createdAt: Date | undefined;
  updatedAt: Date | undefined;
  metadata: string;
  invoice: InvoicePartial | undefined;
}

export interface Invoice {
  id: string;
  displayId: string;
  amount: number;
  currency: Currency;
  externalPayerId: string;
  externalPayerName: string;
  externalPayerType: ExternalPayerType;
  groupId: string;
  groupName: string;
  groupType: GroupType;
  status: string;
  invoiceDate: Date | undefined;
  dueDate: Date | undefined;
  createdAt: Date | undefined;
  updatedAt: Date | undefined;
  metadata: { [key: string]: any } | undefined;
}

export interface ChargeTemplate {
  name: string;
  description: string;
  amount: number;
  currency: Currency;
  externalPayerId: string;
  externalPayerType: ExternalPayerType;
  groupId: string;
  items: ProductItem[];
}

export interface Recurrence {
  recurrenceId: string;
  isActive: boolean;
  recurrenceInterval: RecurrenceInterval;
  startDate: Date | undefined;
  endDate: Date;
  nextOccurrence: Date | undefined;
  lastOccurrence?: Date;
  anyoneCanCancel: boolean;
  updatedAt: Date;
  updatedBy: string;
  status?: RecurrenceStatus;
}

export interface RecurringCharge {
  charge: ChargeTemplate | undefined;
  recurrence: Recurrence | undefined;
}

export interface ChargePartial {
  id: string;
  name: string;
  description: string;
  amount: number;
  currency: Currency;
  status: string;
  items: ProductItem[];
}

export interface ProductItem {
  id?: string;
  product: Product;
  quantity: number;
}

export type Markup = gqlMarkup | MarkupInput;

export type Tax = ProductTax | ProductTaxInput;

export interface ChargeCode {
  id: string;
  chargeCode: string;
  glCode: string;
  description: string;
  groupId: string;
  groupType: GroupType;
  source: ChargeCodeSource;
}

export enum ChargeCodeSource {
  CHARGE_CODE_SOURCE_INTEGRATION = 'CHARGE_CODE_SOURCE_INTEGRATION',
  CHARGE_CODE_SOURCE_ACTIVATE = 'CHARGE_CODE_SOURCE_ACTIVATE',
  UNRECOGNIZED = 'UNRECOGNIZED',
}

export interface CreateChargeCodeRequest {
  chargeCode: string;
  chargeCodeSource: ChargeCodeSource;
  description?: string;
  glCode: string;
  groupId: string;
  groupType: GroupType;
}

/*
 ** @deprecated Please use ProductCategory from the billing-and-payment library for web usage, or ProductCategory from the billing library when working on the server side
 */
export interface ProductCategory {
  productCategoryId: string;
  name: string;
  description: string;
  productType: ProductType;
  groupId: string;
  groupType: GroupType;
}

export interface SellingPrice {
  grossAdminFeeTax?: number | null;
  grossAdminFee?: number | null;
  grossMarkup?: number;
  grossTax?: number;
  listPrice?: number;
  netPrice?: number;
}

export interface Product {
  id: string;
  name: string;
  description: string;
  amount: number;
  groupId: string;
  groupType: GroupType;
  markup?: Markup;
  adminFee?: AdminFee;
  productCategory: ProductCategory;
  tax?: Tax;
  grossTax?: number; // Deprecated
  grossMarkup?: number; // Deprecated
  listPrice?: number; // Deprecated
  netPrice?: number; // Deprecated
  creditAmount?: number;
  chargeCode?: ChargeCode;
  markupChargeCode?: ChargeCode;
  adminFeeChargeCode?: ChargeCode;
  adminFeeTax?: AdminFeeTax;
  originalPrice?: Price;
  receivables?: Receivable[];
  sellingPrice?: SellingPrice;
}

export interface DeleteProductRequest {
  id: string;
  group: Group;
}

export interface DeleteProductExceptionRequest {
  id: string;
  group: Group;
}

export interface InvoiceDetailsResponse {
  accounts: {
    getInvoiceDetails: GetInvoiceDetailsResponse;
  };
}

export interface GetInvoiceDetailsResponse {
  invoice: Invoice | undefined;
  charges: ChargePartial[];
  transaction: Transaction | null;
}

export interface CompanyPayerInfoResponse {
  channel: {
    name: string;
    address: {
      code: string;
      country: string;
      city: string;
      state: string;
      street1: string;
      street2: string;
    };
    profile: {
      name: string;
    };
  };
}

export interface UserPayerInfoResponse {
  user: {
    profile: {
      name: string;
      email: string;
    };
  };
}

export interface UserPayerInfoResponse {}

export interface Pagination {
  start: number;
  perPage: number;
}

export interface PageInfo {
  start: number;
  total: number;
  perPage: number;
}

export interface SortBy {
  key: string;
  dir: string;
}
export interface ListTransactionFilter {
  startDate?: Date;
  endDate?: Date;
  status?: string[];
  paymentType?: string[];
  externalUserId?: string;
  productName?: string;
  transactionId?: string;
}
export interface ListTransactionsRequest {
  groupId: string;
  groupType: GroupType;
  pagination: Pagination | undefined;
  sortBy: SortBy | undefined;
  filters: ListTransactionFilter | undefined;
}

export function paymentMethodFromJSON(object: any): PaymentMethod {
  switch (object) {
    case 0:
    case 'PAYMENT_METHOD_UNKNOWN':
      return PaymentMethod.PAYMENT_METHOD_UNKNOWN;
    case 1:
    case 'PAYMENT_METHOD_CREDIT_CARD':
      return PaymentMethod.PAYMENT_METHOD_CREDIT_CARD;
    case 2:
    case 'PAYMENT_METHOD_CREDITS':
      return PaymentMethod.PAYMENT_METHOD_CREDITS;
    case -1:
    case 'UNRECOGNIZED':
    default:
      return PaymentMethod.UNRECOGNIZED;
  }
}

export enum INVOICE_STATUS {
  INVOICE_PENDING = 'abp.invoiceList.status.pending',
  INVOICE_UNPAID = 'abp.invoiceList.status.unpaid',
  INVOICE_VOID = 'abp.invoiceList.status.void',
  INVOICE_OVERDUE = 'abp.invoiceList.status.overdue',
  INVOICE_PAID = 'abp.invoiceList.status.paid',
  INVOICE_PAYMENTFAILED = 'abp.invoiceList.status.paymentFailed',
  INVOICE_AUTOPAY = 'abp.invoiceList.status.autopay',
}

export enum RECURRING_CHARGE_STATUS {
  CANCELLED = 'abp.subscription.status.cancelled',
  EXPIRED = 'abp.subscription.status.expired',
  ACTIVE = 'abp.subscription.status.active',
}

export enum CHARGE_STATUS {
  CHARGE_PENDING = 'abp.chargeList.status.pending',
  CHARGE_VOIDED = 'abp.chargeList.status.void',
  CHARGE_UNPAID = 'abp.chargeList.status.unpaid',
  CHARGE_OVERDUE = 'abp.chargeList.status.overdue',
  CHARGE_PAID = 'abp.chargeList.status.paid',
}

export interface GroupSettingRequest {
  groupSettingId: string;
  groupId: string;
  groupType: GroupType;
  settings: { [key: string]: any } | undefined;
}

export interface GetGroupSettingRequest {
  groupId: string;
  groupType: GroupType;
}

export interface GroupSettingResponse {
  groupSettingId: string;
  groupId: string;
  groupType: GroupType;
  settings: { [key: string]: any } | undefined;
}

export interface TransactionResponse {
  accounts: {
    transaction: Transaction;
  };
}

export type BillingDetails = {
  billingAddress: BillingAddress;
  companyName: string;
  taxId: string;
};

export interface ListProductsResponse {
  pageInfo: PageInfo;
  products: Product[];
}

export interface ListProductCategoriesResponse {
  pageInfo: PageInfo;
  productCategory: ProductCategory[];
}

export enum TaxPolicy {
  INCLUSIVE = 'INCLUSIVE',
  EXCLUSIVE = 'EXCLUSIVE',
}

export type TaxDetails = {
  taxName: string;
  taxAmountType: TaxAmountType;
  businessTaxName: string;
  businessTaxNumber: string;
  taxAmountValue: number;
  taxDisplayName: string;
  taxNumber: string;
  taxPolicy: TaxPolicy;
};

export type PaymentDetailsType = {
  currencyCode: SupportedPaymentCurrency;
  status: TransactionPaidStatus;
  cashTotal: number;
  cashSubtotal: number;
  taxTotal: number;
  quantity: number;
  products: Product[];
  paymentSource: PaymentSource | undefined;
  companyName: string;
  taxId: string;
};

export function convertTaxAmountTypeEnum(taxType: any, defaultType: any) {
  if (!taxType) {
    return defaultType || TaxAmountType.TaxAmountTypeUnknown;
  }

  switch (taxType) {
    case 1:
    case TaxAmountType.TaxAmountTypeFixed:
      return TaxAmountType.TaxAmountTypeFixed;
    case 2:
    case TaxAmountType.TaxAmountTypePercentage:
      return TaxAmountType.TaxAmountTypePercentage;
    default:
      return defaultType || TaxAmountType.TaxAmountTypeUnknown;
  }
}

export interface UpdateInvoiceAutopaymentDetailsRequest {
  externalUserId: string;
  externalUserSource: ExternalUserSource;
  paymentMethodId: string;
}

export enum AddItemType {
  EXISTING = 'existing',
  CUSTOM = 'custom',
}

export interface AddProductToChargeDetails {
  tableRowId: string;
  tableProductType: AddItemType | undefined;

  productId: string;
  productGroupId: string;
  name: string;
  description: string;
  rate: string;
  markup: {
    type: MarkupAmountType;
    value: string;
  };
  tax: {
    type: TaxAmountType;
    value: string;
  };

  productCategoryId: string;
  productCategoryName: string;
  productCategoryType: ProductType;

  netPrice: string;
  quantity: string;
  total: string;

  chargeCode: string;
  chargeCodeId: string;
  originalPrice?: {
    rate: string;
    markup: {
      type: MarkupAmountType;
      value: string;
    };
    tax: {
      type: TaxAmountType;
      value: string;
    };
  };
  receivables: Receivable[];
  sellingPrice?: SellingPrice;
}

export interface AddProductToContentDetails {
  productId: string;
  productGroupId: string;
  name: string;
  description: string;
  rate: string;
  markup: {
    type: MarkupAmountType;
    value: string;
  };
  tax: {
    type: TaxAmountType;
    value: string;
  };

  productCategoryId: string;
  productCategoryName: string;
  productCategoryType: ProductType;

  listPrice?: string;
  grossTax?: string;
  grossAdminFeeTax?: string;
  netPrice: string;
  total: string;

  creditAmount?: string;
}

export interface NewProductDetails {
  productCategoryId: string;
  productCategoryName: string;
  chargeCodeId?: string;
  markupChargeCodeId?: string;
  adminFeeChargeCodeId?: string;
  name: string;
  description: string;
  rate: string;
  markup?: MarkupType;
  adminFee?: AdminFeeType;
  adminFeeTax?: AdminFeeTaxType;
  adminFeeTaxDefault?: AdminFeeTaxType;
  tax?: TaxType;
  taxDefault?: TaxType;
  netPrice?: string;
  productType: ProductType;
  creditAmount?: string;
}

export interface PriceInput {
  rate: string;
  markup?: {
    type: MarkupAmountType;
    value: string;
  };
  adminFee?: {
    type: AdminFeeAmountType;
    value: string;
  };
  tax?: {
    type: TaxAmountType;
    value: string;
  };
  netPrice?: string;
}

export interface CreditBundle {
  name: string;
  description: string;
  rate: number | null;
  numberOfCredits: number | null;
  taxType: TaxAmountType;
  taxValue: number | null;
}

export interface MarkupType {
  type: MarkupAmountType;
  value: string;
}

export interface AdminFeeType {
  type: AdminFeeAmountType;
  value: string;
}

export interface AdminFeeTaxType {
  type: TaxAmountType;
  value: string;
}

export interface TaxType {
  type: TaxAmountType;
  value: string;
}

export interface Payer {
  id: string;
  type: ExternalPayerType;
}

export interface Group {
  id: string;
  type: GroupType;
}

export interface ExceptionDetails {
  invoicee?: InvoiceeInfo;
  productType: ProductType;
  productCategoryId: string;
  productIds: DropdownItem<string>[] | [];
  applyToAllProducts: boolean;
  rate?: string;
  markup?: MarkupType;
  adminFee?: AdminFeeType;
  tax?: TaxType;
  taxDefault?: TaxType;
}

export function isNewProductDetails(
  details: NewProductDetails | ExceptionDetails
): details is NewProductDetails {
  return 'name' in details;
}

export interface InvoiceeInfo {
  _id: string;
  name: string;
  type: ExternalPayerType;
}

export interface CreateProductExceptionRequest {
  payer: Payer;
  group: Group;
  productCategoryId?: string;
  productIds?: string;
  amount?: number;
  markup?: Markup;
  tax?: Tax;
}

export interface UpdateProductExceptionRequest {
  id: string;
  payer: Payer;
  group: Group;
  productCategoryId?: string;
  productIds?: string;
  amount?: number;
  markup?: Markup;
  tax?: Tax;
}

export interface GetProductExceptionResponse {
  id: string;
  payer: Payer;
  group: Group;
  productCategory?: ProductCategory;
  products?: Product[];
  amount?: number;
  markup?: Markup;
  tax?: Tax;
}

export type DropdownItem<T> = {
  label: string;
  value: T;
  isDisabled?: boolean;
};

export interface FinalPriceForProduct {
  productId: string;
  exceptionId?: string;
  finalPrice: Price;
}

export interface ChargeDetails {
  name: string;
  description: string;
  amount: string;
  currency: Currency;
  frequency: RecurrenceInterval;
  startDate: Date;
  endDateOption: string;
  endDate: Date | undefined;
  paymentDue: string;
  paymentDueDays: number;
  totalCounts: number;
  invoiceType: InvoiceType;
  invoicee: InvoiceeInfo;
}

// TODO: TM-15574 - update the list categories hook to use the domain enum vs the gql enum
export function convertToGqlProductType(
  productType: ProductType
): gqlProductType {
  switch (productType) {
    case ProductType.PRODUCT_TYPE_SERVICE_LABOUR:
      return gqlProductType.ProductTypeServiceLabour;
    case ProductType.PRODUCT_TYPE_SERVICE_MATERIALS:
      return gqlProductType.ProductTypeServiceMaterials;
    case ProductType.PRODUCT_TYPE_CREDIT_BUNDLE:
      return gqlProductType.ProductTypeCreditBundle;
    default:
      return gqlProductType.ProductTypeServiceLabour;
  }
}

export function parseProductType(value: string): ProductType {
  switch (value) {
    case ProductType.PRODUCT_TYPE_SERVICE_LABOUR:
      return ProductType.PRODUCT_TYPE_SERVICE_LABOUR;
    case ProductType.PRODUCT_TYPE_SERVICE_MATERIALS:
      return ProductType.PRODUCT_TYPE_SERVICE_MATERIALS;
    case ProductType.PRODUCT_TYPE_CREDIT_BUNDLE:
      return ProductType.PRODUCT_TYPE_CREDIT_BUNDLE;
    default:
      return ProductType.PRODUCT_TYPE_SERVICE_LABOUR;
  }
}

export type CreditCardBrands =
  | 'discover'
  | 'amex'
  | 'visa'
  | 'mastercard'
  | 'americanexpress';
